import { useEffect, useMemo, useRef, useState } from 'react';
import { SectionCard } from '@/components/DataList';
import { ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import type { SessionUser } from '@/auth/session';
import { quickPayment } from '@/api/endpoints';
import { apiGet, errorMessage, pickList, type JsonMap } from '@/api/client';
import {
  Button,
  Empty,
  ErrorBox,
  Input,
  Loading,
  Money,
  ProSearch,
} from '@/components/ui';
import { str } from '@/utils/format';

type QuickPayScreenProps = {
  user: SessionUser;
  onUserRefresh?: () => void;
};

type ResolvedTarget = {
  user_id?: number;
  name?: string;
  phone?: string;
  wallet_balance?: number | string;
  token?: string;
};

type BarcodeScanResult = { rawValue?: string };
type BarcodeDetectorCtor = new (options: { formats: string[] }) => {
  detect: (source: ImageBitmap | HTMLVideoElement) => Promise<BarcodeScanResult[]>;
};

function barcodeDetectorCtor(): BarcodeDetectorCtor | undefined {
  return (window as Window & { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
}

function qrImageUrl(payload: string, size = 260): string {
  const px = Math.max(72, Math.min(size, 360));
  return `https://api.qrserver.com/v1/create-qr-code/?size=${px}x${px}&margin=8&data=${encodeURIComponent(payload)}`;
}

async function resolvePaymentToken(raw: string) {
  const token = raw.trim();
  const qs = new URLSearchParams({ token }).toString();
  return apiGet(`/quick-payment/resolve?${qs}`);
}

function asBalance(value: unknown): number | string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
}

export function QuickPayScreen({ user, onUserRefresh }: QuickPayScreenProps) {
  const isDistributor = user.role === 'distributor';
  const isStaffSender = user.role === 'admin' || isDistributor;
  const showOwnQr = !isDistributor;

  const [qrData, setQrData] = useState<JsonMap | null>(null);
  const [qrLoading, setQrLoading] = useState(showOwnQr);
  const [qrError, setQrError] = useState('');

  const [tokenInput, setTokenInput] = useState('');
  const [resolved, setResolved] = useState<ResolvedTarget | null>(null);
  const [amount, setAmount] = useState('');
  const [password, setPassword] = useState('');
  const [method, setMethod] = useState<'code' | 'scan'>('code');
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);

  const [recents, setRecents] = useState<JsonMap[]>([]);
  const [recentsLoading, setRecentsLoading] = useState(isDistributor);
  const [customerSearch, setCustomerSearch] = useState('');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function loadQr() {
    if (!showOwnQr) return;
    setQrLoading(true);
    setQrError('');
    const res = await quickPayment.myQr();
    setQrLoading(false);
    if (!res.success) {
      setQrError(errorMessage(res));
      setQrData(null);
      return;
    }
    const data = (res.data && typeof res.data === 'object' ? res.data : res) as JsonMap;
    setQrData(data);
  }

  async function loadRecents() {
    if (!isDistributor) return;
    setRecentsLoading(true);
    const res = await quickPayment.recents();
    setRecentsLoading(false);
    if (res.success) setRecents(pickList(res, 'data', 'recents', 'items'));
  }

  useEffect(() => {
    void loadQr();
    void loadRecents();
  }, [isDistributor, showOwnQr]);

  async function regenerateQr() {
    setBusy(true);
    const res = await quickPayment.regenerate();
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم تحديث الرمز.') : errorMessage(res));
    if (res.success) void loadQr();
  }

  async function resolveToken(raw?: string) {
    const value = String(raw ?? tokenInput).trim();
    if (!value) {
      setMsg('أدخل رمز الدفع أو امسح QR.');
      return;
    }
    setBusy(true);
    setMsg('');
    const res = await resolvePaymentToken(value);
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      setResolved(null);
      return;
    }
    const data = (res.data && typeof res.data === 'object' ? res.data : res) as JsonMap;
    setResolved({
      user_id: Number(data.user_id),
      name: str(data.name),
      phone: str(data.phone),
      wallet_balance: asBalance(data.wallet_balance),
      token: str(data.token ?? value),
    });
    setTokenInput(str(data.token ?? value));
  }

  async function charge() {
    if (!resolved?.token && !tokenInput.trim()) {
      setMsg('حدد الزبون أولاً.');
      return;
    }
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setMsg('أدخل مبلغاً صحيحاً.');
      return;
    }
    if (!password.trim()) {
      setMsg('أدخل كلمة مرور الحساب.');
      return;
    }
    setBusy(true);
    const res = await quickPayment.charge({
      token: resolved?.token ?? tokenInput.trim(),
      amount: numericAmount,
      password,
    });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم تنفيذ الدفع السريع.') : errorMessage(res));
    if (res.success) {
      setAmount('');
      setPassword('');
      setResolved(null);
      setTokenInput('');
      onUserRefresh?.();
      if (isDistributor) void loadRecents();
    }
  }

  async function scanImageFile(file: File | undefined) {
    if (!file || !isStaffSender) return;
    const Detector = barcodeDetectorCtor();
    if (!Detector) {
      setMsg('المسح غير مدعوم على هذا الجهاز. استخدم الإدخال اليدوي.');
      return;
    }
    try {
      const bitmap = await createImageBitmap(file);
      const codes = await new Detector({ formats: ['qr_code'] }).detect(bitmap).catch(() => []);
      bitmap.close?.();
      const raw = codes[0]?.rawValue;
      if (raw) {
        await resolveToken(raw);
        return;
      }
      setMsg('لم يتم العثور على رمز QR واضح في الصورة.');
    } catch {
      setMsg('تعذر قراءة الصورة. جرّب تصوير الرمز بإضاءة أوضح.');
    }
  }

  async function startCameraScan() {
    if (!isStaffSender) return;
    const Detector = barcodeDetectorCtor();
    if (!Detector) {
      setMsg('المسح غير مدعوم على هذا الجهاز. استخدم الإدخال اليدوي.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      fileInputRef.current?.click();
      return;
    }
    setScanning(true);
    setMethod('scan');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      if (videoRef.current) videoRef.current.srcObject = stream;
      const detector = new Detector({ formats: ['qr_code'] });
      let stopped = false;
      const tick = async () => {
        if (stopped || !videoRef.current) return;
        const hits = await detector.detect(videoRef.current).catch(() => []);
        if (hits[0]?.rawValue) {
          stopped = true;
          stream.getTracks().forEach((t) => t.stop());
          setScanning(false);
          await resolveToken(hits[0].rawValue);
          return;
        }
        requestAnimationFrame(tick);
      };
      setTimeout(() => void tick(), 500);
      setTimeout(() => {
        if (!stopped) {
          stopped = true;
          stream.getTracks().forEach((t) => t.stop());
          setScanning(false);
          setMsg('لم يتم التقاط رمز واضح خلال الوقت المحدد.');
        }
      }, 20000);
    } catch (err) {
      setScanning(false);
      const e = err as { name?: string };
      if (e.name === 'NotAllowedError' || e.name === 'SecurityError') {
        setMsg('صلاحية الكاميرا غير مفعّلة. سيتم فتح التصوير من الجهاز كحل بديل.');
        fileInputRef.current?.click();
        return;
      }
      if (e.name === 'NotFoundError' || e.name === 'OverconstrainedError') {
        setMsg('لم يتم العثور على كاميرا مناسبة على هذا الجهاز.');
        return;
      }
      setMsg('تعذر فتح الكاميرا. سيتم فتح التصوير من الجهاز كحل بديل.');
      fileInputRef.current?.click();
    }
  }

  function selectRecentCustomer(row: JsonMap) {
    setResolved({
      user_id: Number(row.user_id),
      name: str(row.name),
      phone: str(row.phone),
      wallet_balance: asBalance(row.wallet_balance),
      token: str(row.token),
    });
    setTokenInput(str(row.token));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const filteredRecents = useMemo(() => {
    const needle = customerSearch.trim().toLowerCase();
    if (!needle) return recents;
    return recents.filter((r) => `${str(r.name)} ${str(r.phone)}`.toLowerCase().includes(needle));
  }, [recents, customerSearch]);

  const frameMode = user.role === 'admin' ? 'admin' : 'customer';
  const pageTitle = isDistributor ? 'إرسال رصيد سريع' : isStaffSender ? 'الدفع السريع' : 'الدفع السريع QR';

  if (showOwnQr && qrLoading) return <Loading />;
  if (showOwnQr && qrError) return <ErrorBox message={qrError} onRetry={() => void loadQr()} />;

  const payload = str(qrData?.payload ?? (qrData?.token ? `ESH_QP:${str(qrData.token)}` : ''));
  const displayToken = str(qrData?.token, payload.replace(/^ESH_QP:/i, ''));

  async function copyPayload() {
    const value = payload || displayToken;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setMsg('تعذر نسخ الرمز.');
    }
  }

  const senderSection = isStaffSender ? (
    <SectionCard
      title={isDistributor ? 'إرسال رصيد سريع' : 'إضافة رصيد عبر QR'}
      subtitle={
        isDistributor
          ? 'اختر طريقة تحديد الزبون ثم أكمل عملية التحويل بأمان.'
          : 'امسح رمز الزبون أو أدخل الرمز يدوياً ثم أدخل المبلغ وكلمة المرور.'
      }
      icon={<Icon name="zap" size={24} />}
    >
      {isDistributor ? (
        <div className="quickpay-method-grid">
          <button
            type="button"
            className={`quickpay-method-card ${method === 'code' ? 'active' : ''}`.trim()}
            onClick={() => setMethod('code')}
          >
            <span className="quickpay-method-icon">
              <Icon name="copy" size={24} />
            </span>
            <span>
              <strong>إدخال رمز الدفع</strong>
              <small>ألصق رمز الزبون وتحقق منه مباشرة.</small>
            </span>
          </button>
          <button
            type="button"
            className={`quickpay-method-card ${method === 'scan' ? 'active' : ''}`.trim()}
            onClick={() => {
              setMethod('scan');
              void startCameraScan();
            }}
          >
            <span className="quickpay-method-icon">
              <Icon name="zap" size={24} />
            </span>
            <span>
              <strong>مسح QR</strong>
              <small>استخدم الكاميرا لقراءة رمز الزبون.</small>
            </span>
          </button>
        </div>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          void scanImageFile(e.currentTarget.files?.[0]);
          e.currentTarget.value = '';
        }}
      />

      {(method === 'code' || !isDistributor) && (
        <div className={`quickpay-code-entry ${isDistributor ? '' : 'top-space'}`.trim()}>
          <Input
            label="رمز الدفع"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="ESH_QP:... أو QP_..."
          />
          <div className="action-row wrap">
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void resolveToken()}>
              <Icon name="search" size={17} /> تحقق من الرمز
            </Button>
            {!isDistributor ? (
              <>
                <Button type="button" disabled={busy} onClick={() => void startCameraScan()}>
                  مسح QR
                </Button>
              </>
            ) : null}
          </div>
        </div>
      )}

      {scanning ? (
        <div className="scanner-box">
          <video ref={videoRef} autoPlay playsInline muted />
        </div>
      ) : null}

      {resolved ? (
        <div className={`${isDistributor ? 'quickpay-selected-target' : 'selected-customer'} top-space`.trim()}>
          {isDistributor ? (
            <>
              <div className="dist-customer-avatar">{str(resolved.name).trim().slice(0, 1) || '؟'}</div>
              <div>
                <small>التحويل إلى</small>
                <strong>{str(resolved.name)}</strong>
                <span dir="ltr">{str(resolved.phone)}</span>
              </div>
              <div className="quickpay-target-balance">
                <small>الرصيد الحالي</small>
                <strong>
                  <Money value={resolved.wallet_balance ?? 0} />
                </strong>
              </div>
            </>
          ) : (
            <>
              <strong>{str(resolved.name)}</strong>
              <span dir="ltr">{str(resolved.phone)}</span>
              <small>
                الرصيد الحالي: <Money value={resolved.wallet_balance ?? 0} />
              </small>
            </>
          )}
        </div>
      ) : null}

      <div className={`form-grid two-columns top-space ${isDistributor ? 'quickpay-money-fields' : ''}`.trim()}>
        <Input
          label="المبلغ"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder="0"
        />
        <Input
          label="كلمة مرور الحساب"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
        />
      </div>

      <Button
        type="button"
        className="big-action top-space"
        disabled={busy || !resolved}
        onClick={() => void charge()}
      >
        {busy ? 'جاري التنفيذ…' : isDistributor ? 'إرسال الرصيد' : 'إضافة الرصيد'}
      </Button>
      {msg ? <p className={msg.includes('تم') ? 'ok' : 'error'}>{msg}</p> : null}
    </SectionCard>
  ) : null;

  return (
    <ScreenFrame mode={frameMode} icon="zap" title={pageTitle}>
      <div className={`stack-gap ${isDistributor ? 'quickpay-pro-page' : ''}`.trim()}>
        {showOwnQr ? (
          <SectionCard
            title="رمز الدفع السريع"
            subtitle="اعرض هذا الرمز للإدارة أو الموزع لإضافة رصيد إلى محفظتك."
            icon={<Icon name="zap" size={24} />}
          >
            <article className="quickpay-qr-card">
              <div className="quickpay-qr-visual">
                <div className="quickpay-qr-frame">
                  {payload ? (
                    <img src={qrImageUrl(payload, 280)} alt="رمز الدفع السريع" />
                  ) : (
                    <span className="muted">جاري تجهيز الرمز…</span>
                  )}
                </div>
                <p className="quickpay-qr-hint">وجّه الكاميرا نحو الرمز للمسح</p>
              </div>

              <div className="quickpay-qr-meta">
                <div className="quickpay-qr-identity">
                  <span className="quickpay-qr-avatar" aria-hidden>
                    <Icon name="user" size={22} />
                  </span>
                  <div>
                    <strong>{user.name}</strong>
                    <small dir="ltr" lang="en">
                      {user.phone}
                    </small>
                  </div>
                </div>

                <div className="quickpay-qr-code-block">
                  <span>رمز الدفع</span>
                  <code dir="ltr" lang="en">
                    {displayToken || '—'}
                  </code>
                </div>

                <div className="form-actions quickpay-qr-actions">
                  <Button type="button" disabled={!payload} onClick={() => void copyPayload()}>
                    <Icon name="check" size={18} />
                    {copied ? 'تم النسخ' : 'نسخ الرمز'}
                  </Button>
                  <Button type="button" variant="secondary" disabled={busy} onClick={() => void regenerateQr()}>
                    <Icon name="refresh" size={18} />
                    تحديث الرمز
                  </Button>
                </div>
              </div>
            </article>
          </SectionCard>
        ) : null}

        {senderSection}

        {isDistributor ? (
          <SectionCard
            title="زبائني"
            subtitle="اختر زبوناً محفوظاً للتحويل إليه مباشرة."
            icon={<Icon name="users" size={24} />}
          >
            <ProSearch value={customerSearch} onChange={setCustomerSearch} placeholder="بحث بالاسم أو الرقم" />
            {recentsLoading ? (
              <Loading />
            ) : (
              <div className="quick-recent-list top-space">
                {filteredRecents.map((row) => (
                  <button
                    key={str(row.user_id)}
                    type="button"
                    className={`quick-recent-row ${resolved?.user_id === Number(row.user_id) ? 'active' : ''}`.trim()}
                    onClick={() => selectRecentCustomer(row)}
                  >
                    <div className="quick-recent-qr">
                      <img
                        alt="QR"
                        src={qrImageUrl(str(row.payload ?? (row.token ? `ESH_QP:${str(row.token)}` : '')), 84)}
                      />
                    </div>
                    <div className="quick-recent-person">
                      <strong>{str(row.name)}</strong>
                      <small dir="ltr">{str(row.phone)}</small>
                    </div>
                    <div className="quick-recent-balance">
                      <small>الرصيد</small>
                      <strong>
                        <Money value={row.wallet_balance ?? 0} />
                      </strong>
                    </div>
                    <span className="quick-recent-action">اختيار</span>
                  </button>
                ))}
                {filteredRecents.length === 0 ? (
                  <Empty title="لا توجد نتائج" hint="أضف زبائن إلى حسابك ليظهروا هنا." />
                ) : null}
              </div>
            )}
          </SectionCard>
        ) : null}
      </div>
    </ScreenFrame>
  );
}
